/**
 * Les ancres des visites guidées (#78) : les valeurs possibles de l'attribut
 * `data-tour` posé sur les éléments que les visites décrivent.
 *
 * Un attribut dédié plutôt qu'une classe ou une structure CSS : une visite ne
 * doit pas casser parce qu'un écran a changé de mise en page. Les écrans et le
 * registre des visites puisent ici les mêmes clés ; un test vérifie que chaque
 * étape vise une ancre déclarée.
 */
export const TOUR_ANCHORS = [
  'dashboard.summary',
  'dashboard.tiles',
  'dashboard.range',
  'dashboard.report',
  'dashboard.trend',
  'dashboard.alerts',
  'dashboard.table',
  'backoffice.help',
  'modules.storage',
  'modules.import',
  'modules.dropzone',
  'modules.search',
  'modules.filters',
  'modules.list',
  'modules.actions',
  'sessions.search',
  'sessions.filters',
  'sessions.new',
  'sessions.list',
  'sessions.status',
  'sessions.trainers',
  'sessions.actions',
  'sessionDetail.header',
  'sessionDetail.edit',
  'sessionDetail.info',
  'sessionDetail.trainers',
  'sessionDetail.kpis',
  'sessionDetail.path',
  'sessionDetail.participants',
  'sessionDetail.filter',
  'sessionDetail.exports',
  'sessionDetail.attendance',
  'sessionDetail.survey',
  'surveyEditor.intro',
  'surveyEditor.questions',
  'surveyEditor.add',
  'surveyEditor.save',
  'sessionDetail.dropout',
  'users.search',
  'users.filters',
  'users.add',
  'users.export',
  'users.table',
  'users.select',
  'users.actions',
  'groups.search',
  'groups.new',
  'groups.list',
  'groups.actions',
  'certificates.templates',
  'certificates.template',
  'certificates.variables',
  'certificates.logo',
  'certificates.background',
  'certificates.email',
  'certificates.preview',
  'certificates.save',
  'questions.filters',
  'questions.table',
  'questions.exports',
  'questions.learners',
  'reports.list',
  'reports.download',
  'reports.editor',
  'reports.granularity',
  'reports.columns',
  'reports.filters',
  'reports.save',
  'reports.schedule',
  'branding.identity',
  'branding.colors',
  'branding.misc',
  'branding.save',
  'trainerCapabilities.intro',
  'trainerCapabilities.domains',
  'trainerCapabilities.save',
  'reminders.cadence',
  'reminders.message',
  'reminders.digest',
  'documents.intro',
  'documents.new',
  'documents.list',
  'quality.intro',
  'quality.tabs',
  'quality.export',
  'quality.new',
  'quality.list',
  'accommodations.intro',
  'accommodations.referent',
  'accommodations.list',
  'complaints.intro',
  'complaints.filters',
  'complaints.export',
  'complaints.new',
  'complaints.list',
  'reminders.convocation',
  'reminders.test',
  'reminders.save',
  'sso.intro',
  'sso.master',
  'sso.picker',
  'sso.provider',
  'sso.save',
  'billing.month',
  'billing.estimate',
  'billing.plan',
  'billing.term',
  'billing.subscribe',
  'billing.manage',
  'billing.profile',
  'billing.history',
  'billing.counters',
  'billing.storage',
  'organizations.create',
  'organizations.list',
  'organizations.actions',
  'trialRequests.intro',
  'trialRequests.list',
  'trialRequests.actions',
  'path.header',
  'path.sheet',
  'path.documents',
  'path.positioning',
  'path.steps',
  'path.focus',
  'player.exit',
  'player.header',
  'player.chapters',
  'player.content',
  'webhooks.intro',
  'webhooks.endpoint',
  'webhooks.save',
  'webhooks.secret',
  'webhooks.deliveries',
  'webhooks.test',
  'learner.dashboard',
  'learner.search',
  'learner.hero',
  'learner.paths',
  'learner.courses',
  'learner.menu',
  'account.profile',
  'account.identity',
  'account.password',
  'account.preferences',
  'account.accommodation',
  'profile.header',
  'profile.indicators',
  'profile.info',
  'profile.groups',
  'profile.sessions',
  'profile.attendance',
  'profile.certificates',
  'profile.positioning',
  'profile.logins',
] as const

export type TourAnchor = (typeof TOUR_ANCHORS)[number]

/** Les props à poser sur l'élément ancré : `<div {...tourAnchor('…')}>`. */
export function tourAnchor(anchor: TourAnchor): { 'data-tour': TourAnchor } {
  return { 'data-tour': anchor }
}

/** Sélecteur CSS d'une ancre, pour la trouver dans le DOM. */
export function anchorSelector(anchor: TourAnchor): string {
  return `[data-tour="${anchor}"]`
}
